import { z } from 'zod';

import type { PartnerUser, TokenPair } from '@/shared/api/types';

const STORAGE_KEY = 'fandsdrop-partner:session';

const sessionSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  user: z.object({
    id: z.string(),
    email: z.string().nullable(),
    handle: z.string(),
    nickname: z.string(),
    displayName: z.string().nullable(),
    role: z.enum(['FAN', 'CREATOR', 'ADMIN']),
  }),
});

export type Session = TokenPair & { user: PartnerUser };

/**
 * 세션이 끝난 이유 — 로그인 화면의 안내 문구와 돌아갈 경로 기억 여부가 달라진다.
 * - logout: 이 탭에서 직접 로그아웃 (돌아갈 경로를 기억하지 않는다)
 * - signed-out-elsewhere: 다른 탭에서 로그아웃
 * - expired: 토큰 갱신 실패
 * - account: 크리에이터가 아니거나 비활성화된 계정 (서버 403)
 */
export type SessionEndReason = 'logout' | 'signed-out-elsewhere' | 'expired' | 'account';

type Listener = () => void;

function readStorage(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = sessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeStorage(session: Session | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 저장소를 쓸 수 없으면(사파리 개인 정보 보호 모드 등) 이 탭 메모리에만 둔다
  }
}

/**
 * 로그인 세션 저장소 — 토큰 쌍과 로그인한 크리에이터 요약.
 *
 * localStorage 에 두어 새로고침·새 탭에서도 유지하고, 다른 탭의 변경(갱신·로그아웃)은 storage 이벤트로 따라간다.
 * React 는 useSyncExternalStore 로 구독한다 (useSession).
 */
function createSessionStore() {
  let session: Session | null = readStorage();
  let endReason: SessionEndReason | null = null;
  const listeners = new Set<Listener>();

  const emit = (): void => {
    listeners.forEach(listener => {
      listener();
    });
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      const next = readStorage();
      if (!next && session) endReason = 'signed-out-elsewhere';
      session = next;
      emit();
    });
  }

  return {
    get: (): Session | null => session,

    /**
     * 저장소를 다시 읽어 메모리 값을 맞춘다 — 다른 탭이 방금 토큰을 갱신했는지 storage 이벤트를 기다리지 않고 확인할 때.
     * (Web Lock 을 얻는 시점과 storage 이벤트가 도착하는 시점은 순서가 보장되지 않는다)
     */
    reload: (): Session | null => {
      const next = readStorage();
      const changed =
        next?.accessToken !== session?.accessToken || next?.refreshToken !== session?.refreshToken;
      if (changed) {
        if (!next && session) endReason = 'signed-out-elsewhere';
        session = next;
        emit();
      }
      return session;
    },

    /** 로그인 성공 — 새 세션을 저장한다 */
    start: (next: Session): void => {
      session = next;
      endReason = null;
      writeStorage(next);
      emit();
    },

    /** 토큰 갱신 — 사용자 정보는 그대로 두고 토큰 쌍만 바꾼다 */
    updateTokens: (tokens: TokenPair): void => {
      if (!session) return;
      session = { ...session, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken };
      writeStorage(session);
      emit();
    },

    /** 세션 종료 — 로그아웃 또는 갱신 실패(만료) */
    end: (reason: SessionEndReason): void => {
      if (!session) return;
      session = null;
      endReason = reason;
      writeStorage(null);
      emit();
    },

    /** 마지막으로 세션이 끝난 이유 (지우지 않는다) */
    peekEndReason: (): SessionEndReason | null => endReason,

    /** 마지막으로 세션이 끝난 이유를 한 번 꺼낸다 (안내 문구를 한 번만 띄우도록 꺼내면 지운다) */
    takeEndReason: (): SessionEndReason | null => {
      const reason = endReason;
      endReason = null;
      return reason;
    },

    subscribe: (listener: Listener): (() => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** 테스트 전용 — 저장소를 다시 읽어 초기 상태로 돌린다 */
    resetForTest: (): void => {
      session = readStorage();
      endReason = null;
      listeners.clear();
    },
  };
}

export const sessionStore = createSessionStore();
